import { MutationCache, type Query, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { showWarningToastAtom, store } from '@/services/globalState';
import { getRateLimitDelay } from '@/util/rateLimit';
import { Sentry } from '@/util/sentry';

type Meta = {
  slowWarningDelay?: number;
  slowWarningTitle?: string;
  slowWarningMessage?: string;
};

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: Meta;
    mutationMeta: Meta;
  }
}

type MutationContext = {
  timer: NodeJS.Timeout;
};

function isContext(context: unknown): context is MutationContext {
  return context !== null && typeof context === 'object';
}

/**
 * Whether a query's last attempt was refused for rate limiting and the
 * server's wait is not over yet.
 *
 * Focus, reconnect and remount refetches check this, because each of them
 * would otherwise send a request the server has already said it will refuse:
 * switching back to the tab mid-wait spent a read and restarted the wait. The
 * notice retries on its own when the wait ends. With no `Retry-After` there is
 * no known wait, so those refetches go ahead as usual.
 */
const isWaitingOutRateLimit = (query: Query) => {
  const { status, error, errorUpdatedAt } = query.state;

  if (status !== 'error') {
    return false;
  }

  const { isRateLimited, seconds } = getRateLimitDelay(error);

  return isRateLimited && !!seconds && Date.now() < errorUpdatedAt + seconds * 1000;
};

/** Refetch on these triggers as usual, except while waiting out a rate limit. */
const unlessWaitingOutRateLimit = (query: Query) => !isWaitingOutRateLimit(query);

const MAX_RETRIES = 3;

/**
 * Status codes where a retry cannot succeed, so the query fails fast instead.
 *
 * 429 is here because retrying a rate limit spends the budget that is already
 * exhausted and pushes recovery further out. The caller reads `retryAfterSeconds`
 * off the `ApiError` and tells the reader when to come back.
 */
const SKIP_RETRY_STATUS_CODES = [400, 401, 403, 404, 429];

const getStatusCode = (error: unknown): number | null => {
  if (typeof error === 'string') {
    return parseInt(error.match(/\sstatus:(\d{3})$/)?.[1] || '', 10) || null;
  }

  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status;

    return typeof status === 'number' ? status : null;
  }

  return null;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: unlessWaitingOutRateLimit,
      refetchOnReconnect: unlessWaitingOutRateLimit,
      refetchOnMount: unlessWaitingOutRateLimit,
      retry: (failureCount, error) => {
        if (failureCount > MAX_RETRIES) {
          return false;
        }

        const statusCode = getStatusCode(error);

        if (statusCode && SKIP_RETRY_STATUS_CODES.includes(statusCode)) {
          Sentry.addBreadcrumb({
            category: 'query',
            message: 'Skipped query retry for status code',
            level: 'info',
            data: { statusCode },
          });

          return false;
        }

        if (failureCount === MAX_RETRIES) {
          Sentry.captureException(error);
        }

        return true;
      },
    },
  },
  mutationCache: new MutationCache({
    onMutate: (_variables, mutation) => {
      const {
        slowWarningTitle,
        slowWarningDelay = 5000,
        slowWarningMessage = '',
      } = mutation.options.meta || {};

      if (!slowWarningTitle) {
        return;
      }

      const timer = setTimeout(() => {
        if (mutation.state.status === 'pending') {
          store.get(showWarningToastAtom)?.({
            title: slowWarningTitle,
            message: slowWarningMessage,
          });
        }
      }, slowWarningDelay);

      return { timer };
    },
    onSettled: (_data, _error, _variables, context) => {
      if (isContext(context)) {
        clearTimeout(context.timer);
      }
    },
  }),
});

export { QueryClientProvider };
